import { ColorsConstants, borderColors, inputColor } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import { TEMPLATE_ENDPOINT } from "../../config/urls";

// Shared, non-component data/helpers for the Workflow/Custom Chatbot/Fallback
// Autoresponder edit drawers (and the Workflows list they're opened from) — anything
// used by more than one of those files belongs here instead of being copy-pasted.
// Shared React components live in ./Components instead (see TriggerRowsEditor).

// ── Trigger-type catalog ───────────────────────────────────────────────────
// Only the "Message-based" entries are implemented today — the rest are shown
// disabled as a preview of trigger types planned for future support.
export const ENTRY_TYPE_GROUPS = [
  {
    label: "Message-based",
    options: [
      { value: "any_message", label: "Any message" },
      { value: "text", label: "Text keyword" },
      { value: "template_button_reply", label: "Template button reply" },
    ],
  },
  {
    label: "Flow and journey",
    options: [
      { value: "whatsapp_flow_id", label: "WhatsApp Flow ID", disabled: true },
      { value: "handoff_from_bot", label: "Handoff from another active bot", disabled: true },
    ],
  },
  {
    label: "Ad and entry points",
    options: [
      { value: "ctwa_ad_id", label: "CTWA / Ad ID", disabled: true },
    ],
  },
];

// Mirrors the backend's NODEBOT_MAX_TRIGGERS env var (default 8) — the server is the
// source of truth and re-validates this; this just keeps the UI from letting a user
// add more rows than the save will ever accept.
export const MAX_TRIGGERS = 8;

// Mirrors the backend's NODEBOT_MAX_LABELS / NODEBOT_LABEL_MAX_LENGTH env vars (see
// _validate_labels in nodebot/views.py) — the server re-validates both; this just
// keeps the inline labels editor (Components/LabelsCell) from accepting more than the
// save will ever accept.
export const MAX_LABELS = 8;
export const MAX_LABEL_LENGTH = 50;

// How a text trigger's keyword is compared against an incoming message — mirrors
// Trigger.MatchMode/TextTriggerType in the backend (nodebot/models.py,
// nodebot/trigger_types.py). "Regex pattern" isn't implemented server-side yet, so it's
// shown disabled here rather than omitted, as a preview of what's coming.
export const MATCH_MODE_OPTIONS = [
  { value: "exact", label: "Exact match" },
  { value: "case_insensitive", label: "Case insensitive" },
  { value: "contains", label: "Contains" },
  { value: "regex", label: "Regex pattern", disabled: true },
];

// "Who can trigger this bot" — UI-only preview for now, same as the other disabled
// entries above: no model field, no API support, not enforced anywhere in the message
// dispatcher. Only "anyone" (today's actual, only behavior) is enabled; the rest are
// shown disabled as a preview of audience targeting planned for future support.
export const AUDIENCE_FILTER_OPTIONS = [
  { value: "anyone", label: "Anyone who pings this WABA number" },
  { value: "all_contacts", label: "All existing contacts", disabled: true },
  { value: "new_contacts", label: "New contacts only (first message ever)", disabled: true },
  { value: "specific_tag", label: "Specific tag", disabled: true },
  { value: "specific_segment", label: "Specific segment", disabled: true },
];

// ── Shared field styling ────────────────────────────────────────────────────
export const selectFieldSx = {
  borderRadius: "8px",
  height: 40,
  color: inputColor,
  ...typography.bodyLarge,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: borderColors.inputFieldGrey,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: ColorsConstants.primaryGray50,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: ColorsConstants.primaryGray100,
  },
};

// Single-line variant — used by the trigger-value text field and the template/button
// pickers. Fallback Autoresponder's Name/Message fields need their own multiline
// variant instead (a fixed height:40 would break `rows={4}` textareas), so that one
// stays local to FallbackAutoresponderEdit.js rather than living here.
export const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    height: 40,
    color: inputColor,
    ...typography.bodyLarge,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: borderColors.inputFieldGrey,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: ColorsConstants.primaryGray50,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: ColorsConstants.primaryGray100,
    },
  },
  "& input": {
    padding: "8px 14px",
    ...typography.bodyLarge,
    color: inputColor,
  },
};

export const labelSx = {
  ...typography.bodySmall,
  color: "#6C7C93",
  mb: 0.75,
  fontWeight: 500,
};

export const sectionLabelSx = {
  ...typography.bodySmall,
  fontWeight: 600,
  color: "#6C7C93",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  mb: 2,
};

// ── Trigger row helpers ─────────────────────────────────────────────────────
export const emptyTextRow = (key) => ({
  key,
  type: "text",
  value: "",
  matchMode: "exact",
  templateMode: "specific",
  selectedTemplate: null,
  selectedButton: null,
  pendingTemplateId: null,
  pendingButtonId: null,
});

// Maps a Nodebot/CustomChatbot's `triggers` API field into the row shape the editor
// state (and TriggerRowsEditor) works with. `selectedTemplate`/`selectedButton` start
// null and get resolved once the template list loads (see the "pending" fields).
// A "text" row with match_mode "any" is "Any Text" — its value is always the server's
// fixed sentinel, so it's never carried into the row's `value` (which only ever holds
// a real, user-typed keyword) to avoid it showing up if the user switches the row back
// to "Specific Keywords".
// A "template_button_reply" row with no template_id/button_id is "Any Template" — the
// user typed the button text directly instead of picking a template + button, so
// `value` (the button text) IS carried into the row, same as a real text keyword.
export const mapApiTriggersToRows = (apiTriggers, nextKey) =>
  (Array.isArray(apiTriggers) ? apiTriggers : []).map((t) => {
    const isAnyTemplate = t.type === "template_button_reply" && !t.template_id && !t.button_id;
    return {
      key: nextKey(),
      type: t.type,
      value:
        (t.type === "text" && t.match_mode !== "any") || isAnyTemplate ? t.value || "" : "",
      matchMode: t.match_mode || "exact",
      templateMode: isAnyTemplate ? "any" : "specific",
      selectedTemplate: null,
      selectedButton: null,
      pendingTemplateId: t.template_id || null,
      pendingButtonId: t.button_id || null,
    };
  });

// A trigger row that matches everything within its scope on its own — "Any Message"
// (every incoming message, any type) or "Any Text" (every incoming text message, a
// sub-type of Text Keyword) — so combining it with other triggers on the same bot adds
// no matching power. Mirrors _catchall_label in nodebot/views.py.
export const isCatchAllRow = (row) =>
  row.type === "any_message" || (row.type === "text" && row.matchMode === "any");

// Inverse of mapApiTriggersToRows — row state back into the shape the save payload's
// `triggers` field expects.
export const buildTriggerPayload = (triggers) =>
  triggers.map((row) => {
    if (row.type === "text") {
      if (row.matchMode === "any") {
        // "Any Text" — no user-typed value; the server always forces its own fixed
        // sentinel (see Trigger.ANY_TEXT_VALUE / _validate_triggers in nodebot/views.py).
        return { type: "text", match_mode: "any" };
      }
      return { type: "text", value: row.value.trim(), match_mode: row.matchMode };
    }
    if (row.type === "any_message") {
      // No user-typed value — the server always forces its own fixed sentinel
      // (see Trigger.ANY_MESSAGE_VALUE / _validate_triggers in nodebot/views.py).
      return { type: "any_message" };
    }
    if (row.templateMode === "any") {
      // "Any Template" — the user typed the button text directly rather than picking a
      // template + button; no template/button reference is stored, and match_mode is
      // configurable exactly like a Text Keyword trigger (see
      // TemplateButtonReplyTriggerType in nodebot/trigger_types.py).
      return {
        type: "template_button_reply",
        value: row.value.trim(),
        match_mode: row.matchMode,
        is_any_template: true,
      };
    }
    return {
      type: "template_button_reply",
      value: row.selectedButton?.text || "",
      template_id: row.selectedTemplate?.id,
      button_id: row.selectedButton?.id,
    };
  });

// Normalizes trigger rows for change-detection (dirty tracking) — deliberately ignores
// `key` and object identity, since `selectedTemplate`/`selectedButton` get replaced by a
// freshly-resolved object once the template list loads after mount, even when the user
// hasn't touched the row. Comparing this fingerprint before/after avoids flagging that
// resolution as an unsaved edit.
export const triggersFingerprint = (triggers) =>
  JSON.stringify(
    triggers.map((row) => ({
      type: row.type,
      value: row.type === "text" || (row.type === "template_button_reply" && row.templateMode === "any") ? row.value : "",
      matchMode: row.matchMode,
      templateMode: row.templateMode,
      templateId: row.selectedTemplate?.id ?? row.pendingTemplateId ?? null,
      buttonId: row.selectedButton?.id ?? row.pendingButtonId ?? null,
    }))
  );

// Returns an error message string, or null if `triggers` is valid. `subjectLabel`
// only affects the max-triggers message's wording (e.g. "nodebot" vs "custom chatbot").
export const validateTriggerRows = (triggers, subjectLabel = "bot") => {
  if (triggers.length === 0) {
    return "At least one trigger is required";
  }
  if (triggers.length > MAX_TRIGGERS) {
    return `A ${subjectLabel} can have at most ${MAX_TRIGGERS} triggers`;
  }

  // A catch-all row (Any Message or Any Text) matches everything within its scope on
  // its own — combining it with other triggers on the same bot adds no matching power
  // and would wrongly suggest the rows have some priority order between them, which
  // doesn't exist. Mirrors the same rule enforced server-side in nodebot/views.py's
  // _validate_triggers.
  if (triggers.length > 1 && triggers.some(isCatchAllRow)) {
    return 'A catch-all trigger ("Any Message" or "Any Text") can\'t be combined with other triggers on the same bot';
  }

  const seen = new Set();
  for (const row of triggers) {
    if (isCatchAllRow(row)) continue; // no value/template to validate — exclusivity already checked above

    if (row.type === "text") {
      if (!row.value.trim()) return "Every text trigger needs a keyword";
      if (row.value.includes(","))
        return "Only one keyword is allowed per trigger — remove the comma and add each keyword as its own trigger";
    } else if (row.templateMode === "any") {
      if (!row.value.trim()) return "Every 'Any Template' trigger needs button text";
    } else if (!row.selectedTemplate || !row.selectedButton) {
      return "Every template button trigger needs a template and a button";
    }

    const value =
      row.type === "text" || row.templateMode === "any" ? row.value.trim() : row.selectedButton?.text;
    const combo = `${row.type}:${value}`;
    if (seen.has(combo)) return `Trigger "${value}" is used more than once`;
    seen.add(combo);
  }
  return null;
};

// ── Approved WhatsApp templates (for the Template Button Reply pickers) ────
export const fetchApprovedTemplateOptions = async (getValidToken, tenant) => {
  const token = await getValidToken();
  const response = await fetch(`${TEMPLATE_ENDPOINT}?limit=1000&ordering=-last_edited`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    },
  });
  if (!response.ok) throw new Error("Failed to fetch templates");
  const jsonData = await response.json();
  const approved = (jsonData.results || []).filter(
    (template) => template.status === "APPROVED" && template.active
  );

  return approved.map((item) => {
    const allButtons = item?.template_preview_json?.allButtons;
    return {
      id: item.template_id,
      name: item.template_name,
      buttons: Array.isArray(allButtons)
        ? allButtons
            .filter((button) => button.type === "custom")
            .map((button) => ({
              id: button?.id,
              text: button?.data?.buttonText || "",
            }))
        : [],
    };
  });
};
